import { useState } from "react"
import { useTranslation } from "react-i18next"
import { MagnifyingGlass } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Card, CardBody, CardHeader, CardHeaderTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { IdentifierValue } from "@/modules/admin/advanced/components/identifier-value"
import { parseSofiaLink } from "@/modules/admin/advanced/lib/parse-sofia-link"
import { fetchChatLinkIdentifiers } from "@/modules/admin/advanced/services/chatLinkIdentifiersQuery"
import { fetchMedicalRecordLinkIdentifiers } from "@/modules/admin/advanced/services/medicalRecordLinkIdentifiersQuery"

type LinkResolution =
  | { status: "idle" }
  | { status: "unrecognizedLink" }
  | { status: "patientNodeIdInvalid" }
  | { status: "chatThreadNotFound" }
  | { status: "requestFailed" }
  | { status: "resolved"; patientId: string; appointmentId: string | null; isAppointmentNodeIdInvalid: boolean }

const resolveSofiaLink = async (link: string): Promise<LinkResolution> => {
  const parsedLink = parseSofiaLink(link)

  if (parsedLink.kind === "unrecognized") {
    return { status: "unrecognizedLink" }
  }

  if (parsedLink.kind === "chat") {
    const chatLinkIdentifiers = await fetchChatLinkIdentifiers(parsedLink.chatThreadId)
    if (!chatLinkIdentifiers) {
      return { status: "chatThreadNotFound" }
    }
    return {
      status: "resolved",
      patientId: chatLinkIdentifiers.patientId,
      appointmentId: String(chatLinkIdentifiers.appointmentId),
      isAppointmentNodeIdInvalid: false,
    }
  }

  const medicalRecordLinkIdentifiers = await fetchMedicalRecordLinkIdentifiers(
    parsedLink.patientNodeId,
    parsedLink.appointmentNodeId,
  )
  if (!medicalRecordLinkIdentifiers?.patient) {
    return { status: "patientNodeIdInvalid" }
  }
  return {
    status: "resolved",
    patientId: medicalRecordLinkIdentifiers.patient.internalId,
    appointmentId: medicalRecordLinkIdentifiers.appointment?.internalId ?? null,
    isAppointmentNodeIdInvalid: parsedLink.appointmentNodeId !== null && !medicalRecordLinkIdentifiers.appointment,
  }
}

export const SofiaLinkIdentifiers = () => {
  const { t } = useTranslation()
  const [link, setLink] = useState("")
  const [isResolving, setIsResolving] = useState(false)
  const [resolution, setResolution] = useState<LinkResolution>({ status: "idle" })

  const handleSubmit = async (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!link.trim()) {
      return
    }

    setIsResolving(true)
    try {
      setResolution(await resolveSofiaLink(link))
    } catch {
      setResolution({ status: "requestFailed" })
    } finally {
      setIsResolving(false)
    }
  }

  const renderAppointmentId = (resolvedLink: Extract<LinkResolution, { status: "resolved" }>) => {
    if (resolvedLink.appointmentId !== null) {
      return (
        <IdentifierValue
          label={t("admin.advanced.sofiaLinkIdentifiers.appointmentId")}
          value={resolvedLink.appointmentId}
          testId="sofia-link-appointment-id"
        />
      )
    }

    return (
      <p className="text-on-surface-subdued text-sm" data-testid="sofia-link-appointment-id-message">
        {resolvedLink.isAppointmentNodeIdInvalid
          ? t("admin.advanced.sofiaLinkIdentifiers.appointmentNodeIdInvalid")
          : t("admin.advanced.sofiaLinkIdentifiers.appointmentNotInLink")}
      </p>
    )
  }

  return (
    <Card className="h-fit w-120" data-testid="sofia-link-identifiers-card">
      <CardHeader>
        <CardHeaderTitle className="flex items-center gap-4">
          {t("admin.advanced.sofiaLinkIdentifiers.title")}
        </CardHeaderTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-4 p-4 pt-0">
        <form className="flex items-center gap-2" onSubmit={handleSubmit}>
          <Input
            label={t("admin.advanced.sofiaLinkIdentifiers.inputLabel")}
            value={link}
            onChange={(event) => setLink(event.target.value)}
            testId="sofia-link"
          />
          <Button
            type="submit"
            variant="tertiary"
            className="p-2"
            aria-label={t("admin.advanced.sofiaLinkIdentifiers.resolve")}
            isLoading={isResolving}
            disabled={isResolving || !link.trim()}
            data-testid="sofia-link-resolve-button">
            <MagnifyingGlass className="text-on-surface-subdued" />
          </Button>
        </form>
        {resolution.status === "resolved" ? (
          <div className="flex flex-col gap-2">
            <IdentifierValue
              label={t("admin.advanced.sofiaLinkIdentifiers.patientId")}
              value={resolution.patientId}
              testId="sofia-link-patient-id"
            />
            {renderAppointmentId(resolution)}
          </div>
        ) : null}
        {resolution.status !== "idle" && resolution.status !== "resolved" ? (
          <p className="text-critical text-sm" data-testid="sofia-link-message">
            {t(`admin.advanced.sofiaLinkIdentifiers.${resolution.status}`)}
          </p>
        ) : null}
      </CardBody>
    </Card>
  )
}
